import React, { useState, useEffect } from 'react';
import { MapPin, User } from 'lucide-react';
import { ABORT_ERROR, makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { getFullAddress } from '../../helpers/global';
import { sanitizeRichTextHtml } from '../../helpers/safeHtml';
import {
  AssociationDetailSkeleton,
  PropertyAddressesSkeleton,
} from './HoaDirectorySkeletons';
import { resolveAssociationManagementType } from './HoaDirectoryHelper';

const AssociationDetailPanel = ({ associationId, refreshKey = 0 }) => {
  const [association, setAssociation] = useState(null);
  const [loadingAssociation, setLoadingAssociation] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    if (!associationId) {
      setAssociation(null);
      setOrders([]);
      setLoadingAssociation(false);
      setLoadingOrders(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    setAssociation(null);
    setOrders([]);
    setLoadingAssociation(true);
    setLoadingOrders(true);

    Promise.all([
      // Load association details
      makeRESTApiRequest({
        endpoint: ENDPOINTS.ASSOCIATION_WITH_ID(associationId),
        method: 'GET',
        signal: controller.signal,
      }).then(({ response, error }) => {
        if (cancelled || error === ABORT_ERROR) return;
        if (!error && response) {
          setAssociation(response);
        } else if (error) {
          console.error('Failed to load association:', error);
          setAssociation(null);
        }
        setLoadingAssociation(false);
      }),
      
      // Load property addresses
      makeRESTApiRequest({
        endpoint: ENDPOINTS.ASSOCIATION_ORDERS(associationId),
        method: 'GET',
        signal: controller.signal,
      }).then(({ response, error }) => {
        if (cancelled || error === ABORT_ERROR) return;
        if (!error && response) {
          setOrders(response || []);
        } else {
          console.error('Failed to load orders:', error);
          setOrders([]);
        }
        setLoadingOrders(false);
      }),
    ]).catch(err => {
      if (cancelled) return;
      console.error('Failed to load association details:', err);
      setAssociation(null);
      setOrders([]);
      setLoadingAssociation(false);
      setLoadingOrders(false);
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [associationId, refreshKey]);

  const PropertyAddressesSection = () => {
    if (loadingOrders) {
      return <PropertyAddressesSkeleton />;
    }

    if (!orders.length) {
      return null;
    }

    return (
      <div className="p-3">
        <div className="text-muted tinyFont d-flex align-items-center">
          <MapPin size={14} className="mr-1" /> Property Addresses ({orders.length})
        </div>
        <div className="text-dark smallFont mt-2 d-flex flex-column">
          {orders.map(order => {
            const address = getFullAddress(order.service);
            return address ? (
              <a
                key={order._id}
                href={`/services/${order.service._id}`}
                className="mb-1 px-2 py-1 bg-light-grey rounded-pill text-decoration-none text-dark d-inline-block align-self-start"
                target="_blank"
                rel="noreferrer"
              >
                {address}
              </a>
            ) : null;
          })}
        </div>
      </div>
    );
  };

  if (!associationId) {
    return (
      <div
        className="bg-white border shadow-sm overflow-hidden d-flex flex-column"
        style={{ borderRadius: 10 }}
      >
        <div className="text-center py-5 px-4 text-muted smallFont">
          Select an association to see full details
        </div>
      </div>
    );
  }

  if (loadingAssociation) {
    return <AssociationDetailSkeleton />;
  }

  if (!association) {
    return (
      <div
        className="bg-white border shadow-sm overflow-hidden d-flex flex-column"
        style={{ borderRadius: 10 }}
      >
        <div className="text-center py-5 px-4 text-muted smallFont">
          Unable to load association details
        </div>
      </div>
    );
  }

  const renderField = (label, value) => (
    <div className="mb-2">
      <div className="text-muted tinyFont">{label}</div>
      <div className="fw-medium text-dark smallFont text-break">{value || 'N/A'}</div>
    </div>
  );

  const ContactsSection = ({ contacts }) => {
    if (!contacts || contacts.length === 0) return null;

    return (
      <div className="">
        <h4 className="mt-3 mb-1 xtinyFont text-muted">
          <User size={12} className="mr-1" />
          CONTACTS ({contacts.length})
        </h4>
        <div>
          {contacts.map((contact, index) => {
            const contactInfo = [contact.directPhone, contact.directEmail]
              .filter(Boolean)
              .join(' · ');
            const isLast = index === contacts.length - 1;
            return (
              <div
                key={contact._id}
                className={`py-2 tinyFont ${isLast ? '' : ' border-bottom'}`}
              >
                <div className="fw-semibold text-dark">
                  <span className="fw-bold"> {contact.name || 'N/A'}</span>
                  {contact.title && (
                    <span className="text-muted"> — {contact.title}</span>
                  )}
                </div>
                {contactInfo && (
                  <div className="text-muted tinyFont text-break">{contactInfo}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const hasMgmtCompany = Boolean(association.mgmtId);
  const managementType = resolveAssociationManagementType(association);

  return (
    <>
      <div className="bg-white border shadow-sm overflow-hidden" style={{ borderRadius: 10 }}>
        {/* Header */}
        <div className="px-3 py-3" style={{ background: '#1F4E8C' }}>
          <h3 className="fw-bold text-white smallFont mb-0 text-truncate">{association.name}</h3>
        </div>

        {/* Association Info Section */}
        <div className="px-3 py-3 border-bottom">
          <h4 className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-3 tinyFont text-muted fw-bold">
            ASSOCIATION INFO
            <span className="mt-1 mt-md-0 ml-0 ml-md-2 px-2 py-1 xtinyFont fw-bold bg-green-light text-success rounded-lg">
              CLIENT-VISIBLE
            </span>
          </h4>
          <div className="row">
            <div className="col-12 col-md-6">{renderField('Phone', association.phone)}</div>
            <div className="col-12 col-md-6">{renderField('Email', association.email)}</div>
          </div>

          <ContactsSection contacts={association.contacts} />
        </div>

        {/* Management Section */}
        <div className="px-3 py-3 border-bottom">
          <h4 className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-3 tinyFont text-muted fw-bold">
            MANAGEMENT
            <span
              className="mt-1 mt-md-0 ml-0 ml-md-2 px-2 py-1 xtinyFont fw-bold rounded-lg"
              style={{
                background: '#fff3e0',
                color: '#e65100'
              }}
            >
              PROCESSOR-ONLY
            </span>
          </h4>

          {managementType ? (
            <div className="d-inline-flex border rounded mb-3" style={{ overflow: 'hidden' }}>
              {[
                { key: 'managed', label: 'Has management company' },
                { key: 'self', label: 'Self-managed' }
              ].map(({ key, label }) => {
                const isActive = managementType === key;
                return (
                  <span
                    key={key}
                    className="px-3 py-1 tinyFont text-break"
                    style={{
                      backgroundColor: isActive ? '#1F4E8C' : '',
                      color: isActive ? 'white' : 'var(--gray)'
                    }}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="text-muted smallFont font-italic mb-3">Management type not set.</div>
          )}

          {managementType === 'managed' && (
            <>
              {hasMgmtCompany ? (
                <>
                  {renderField('Company Name', association.mgmtId.name)}
                  <div className="row">
                    <div className="col-12 col-md-6">
                      {renderField('Phone', association.mgmtId.phone)}
                    </div>
                    <div className="col-12 col-md-6">
                      {renderField('Email', association.mgmtId.email)}
                    </div>
                  </div>
                  {association.mgmtId.orderingNote && (
                    <div className="mt-2">
                      <div className="text-muted tinyFont">How to Order</div>
                      <div
                        className="text-dark smallFont instruction-content"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeRichTextHtml(association.mgmtId.orderingNote),
                        }}
                      />
                    </div>
                  )}
                  <ContactsSection contacts={association.mgmtId.contacts} />
                </>
              ) : (
                renderField('Company Name', null)
              )}
              {association.orderingPlatform &&
                renderField('Ordering Platform', association.orderingPlatform)}
            </>
          )}

          {managementType === 'self' && (
            <>
              {association.orderingPlatform &&
                renderField('Ordering Platform', association.orderingPlatform)}
              {association.selfNote && (
                <div>
                  <div className="text-muted tinyFont">Ordering Instructions</div>
                  <div
                    className="text-dark smallFont instruction-content"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeRichTextHtml(association.selfNote),
                    }}
                  />
                </div>
              )}
            </>
          )}

        </div>

        {/* Property Addresses Section */}
        <PropertyAddressesSection />
      </div>
    </>
  );
};

export default AssociationDetailPanel;
